<template>
  <div ref="root" class="wrap" >
    <svg width="100%" height="100%" viewBox="0 0 270 270">
      <!-- 1. 白色分割线 -->
      <defs>
        <g id="whiteLines">
          <line
            v-for="i in 8"
            :key="`l${i}`"
            :x1="135"
            :y1="135"
            :x2="135 + outerR * Math.cos((i * 45 - 90) * Math.PI / 180)"
            :y2="135 + outerR * Math.sin((i * 45 - 90) * Math.PI / 180)"
            stroke="#fff"
            stroke-width="4"
          />
        </g>
        <!-- 中心白色圆环 -->
        <circle id="centerWhiteRing" cx="135" cy="135" :r="imgR + whiteGap" fill="#fff" />
        <!-- 图片裁剪圆 -->
        <clipPath id="imgClip">
          <circle cx="135" cy="135" :r="imgR" />
        </clipPath>
      </defs>

      <!-- 2. 8 个扇形 + 图标 -->
      <g>
        <g
          v-for="(d, i) in paths"
          :key="i"
          class="sector"
          :class="[`color-${i & 1}`, { active: hoverIndex === i }]"
          @mouseenter="activeHover(i)"
          @mouseleave="cancelHover"
          @hover="$emit('select', i)"
        >
          <path :d="d" />
          <!-- 图标 -->
          <image
            v-if="icons[i]"
            :x="iconPos[i]?.x - 16"
            :y="iconPos[i]?.y - 16"
            width="32"
            height="32"
            :href="icons[i]"
            preserveAspectRatio="xMidYMid slice"
            style="pointer-events:none"
          />
        </g>
        <use href="#whiteLines" />
      </g>

      <!-- 3. 中心白色圆环 -->
      <use href="#centerWhiteRing" />

      <!-- 4. 中心图片 -->
      <image
        :x="135 - imgR"
        :y="135 - imgR"
        :width="imgR * 2"
        :height="imgR * 2"
        :href="centerImage"
        clip-path="url(#imgClip)"
        preserveAspectRatio="xMidYMid slice"
        class="center-img"
      />
    </svg>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, defineEmits } from 'vue'

const props = defineProps({
  centerImage: { type: String, required: true },
  icons: { type: Array, default: () => [] }
})

const outerR = 120
const innerR = 75
const imgR = 66
const whiteGap = 10
const hoverIndex = ref(null)

const emit = defineEmits(['ready', 'select', 'cancel', 'radius'])

const paths = computed(() => {
  const angle = 360 / 8
  return Array.from({ length: 8 }, (_, i) => {
    const a1 = i * angle
    const a2 = (i + 1) * angle
    return `
      M ${polar(outerR, a1)}
      A ${outerR} ${outerR} 0 0 1 ${polar(outerR, a2)}
      L ${polar(innerR, a2)}
      A ${innerR} ${innerR} 0 0 0 ${polar(innerR, a1)}
      Z`
  })
})

const iconPos = computed(() => {
  const midR = (innerR + outerR) / 2
  const angle = 360 / 8
  return Array.from({ length: 8 }, (_, i) => {
    const mid = (i + 0.5) * angle
    return {
      x: 135 + midR * Math.cos((mid - 90) * Math.PI / 180),
      y: 135 + midR * Math.sin((mid - 90) * Math.PI / 180)
    }
  })
})
/* 外径 */
const outerArcPos = computed(() => {
  const angle = 360 / 8
  return Array.from({ length: 8 }, (_, i) => {
    const mid = (i + 0.5) * angle
    const rad = (mid - 90) * Math.PI / 180
    return {
      x: 135 + outerR * Math.cos(rad),   // 270 坐标系
      y: 135 + outerR * Math.sin(rad)
    }
  })
})
const root = ref(null)
defineExpose({
  el: root,
  iconPos: iconPos,
  outerArcPos,
  active,
  cancel
})

onMounted(() => {
  emit('radius', {          // ① 半径
    value: outerR,
    pos: outerArcPos.value,
    canvas: { x: 0, y: 0, size: 270 }
  })
  emit('ready')
})

function activeHover (i) {
  active(i)
  emit('select', hoverIndex.value)
}
function cancelHover () {
  cancel()
  emit('cancel')
}
function active (i) {
  hoverIndex.value = i
}
function cancel () {
  hoverIndex.value = null
}
function polar (r, deg) {
  const rad = (deg - 90) * Math.PI / 180
  const x = 135 + r * Math.cos(rad)
  const y = 135 + r * Math.sin(rad)
  return `${x} ${y}`
}
</script>

<style scoped>
.wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -2.5vw;
}
svg {
  overflow: visible;
}
.sector {
  stroke: #fff;
  stroke-width: 4;
  cursor: pointer;
  transition: fill 0.2s ease;
}
.sector.color-0 { fill: #00B0F0; }
.sector.color-1 { fill: #1477d2; }
.sector:hover,
.sector.active {
  fill: #2ca6e0;
}
.center-img {
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
/* ========== 移动端增量 @media ========== */
@media (max-width: 768px) {
  .wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -15vw;
  }
}
</style>
